import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { RegisterState } from 'src/app/common-components/register/models/register-state';
import {
   createRegisterAdapter,
   RegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { accountIdChange } from 'src/app/store/select-account/select-account.actions';
import { ExtensionRegisterOptions } from '../models/extension-register-options';
import { ExtensionRegisterListItem } from '../models/extension-register-results.model';
import { ExtensionTypeOption } from '../models/extension-type-option.interface';
import { ExtensionsFilterOption } from '../models/extensions-filter-option';
import * as extensionRegisterActions from './extension-register.actions';
import {
   createExtension,
   extensionRegisterAdapterActions,
   extensionRegisterInitialFilter,
} from './extension-register.actions';

export const extensionRegisterFeatureKey = 'extensionRegister';

export interface ExtensionRegisterState
   extends RegisterState<ExtensionRegisterListItem, ExtensionRegisterOptions, OrderingOption> {
   filters: ExtensionsFilterOption[];
   extensionTypeOption: ExtensionTypeOption;
}

const adapter: RegisterAdapter<ExtensionRegisterListItem, ExtensionRegisterOptions, OrderingOption> =
   createRegisterAdapter<ExtensionRegisterListItem, ExtensionRegisterOptions, OrderingOption>({
      selectId: (extensionRegisterItem) => {
         return extensionRegisterItem.extensionId ?? 0;
      },
   });

export const initialState: ExtensionRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   extensionTypeOption: { extensionTypeId: 0, name: '', displayName: '', extensions: [], extensionGroups: [] },
   orderingOptions: [{ direction: OrderingDirections.Asc, field: 'number' }],
};

export const extensionRegisterReducer = adapter.createReducer(
   extensionRegisterActions.extensionRegisterAdapterActions,
   initialState,
   on(extensionRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(extensionRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         extensions: action.items,
         hasNextPage: action.hasNextPage,
         totalCount: action.totalCount,
      };
   }),
   on(extensionRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         filters: action.filters,
      };
   }),
   on(extensionRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(createExtension, (state, action) => {
      return {
         ...state,
         extensionTypeOption: action.createExtensionTypeOption ?? { extensionTypeId: 0, name: '' },
      };
   }),
   on(extensionRegisterActions.extensionRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   }),
   on(extensionRegisterActions.setOrderingOptionsFromSessionStorage, (state, action) => {
      return {
         ...state,
         orderingOptions: action.orderingOptions,
      };
   }),
   on(extensionRegisterAdapterActions.loadIdPageSuccess, (state, action) => {
      return {
         ...state,
         ids: [...(state.ids as number[]), ...(action.ids as number[])],
      };
   }),
   on(accountIdChange, (state, action) => {
      return {
         ...state,
         quickFilters: null,
      };
   })
);
export const extensionRegisterSelector = createFeatureSelector<ExtensionRegisterState>(extensionRegisterFeatureKey);
export const selectors = adapter.getSelectors(extensionRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectQuickFilters,
   selectEndCursor,
   selectOrderingOptions,
   selectIds,
} = adapter.getSelectors(extensionRegisterSelector);
